import { SparklesIcon } from '@heroicons/react/24/outline';
import { memo, useMemo } from 'react';

import type { ReadAnimal } from '@/services/api/subjectsService';
import { enhancedApi } from '@/services/api/subjectsService';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';

export type Animal = ReadAnimal;

export const AnimalTooltipContent = memo<EntityTooltipContentProps<Animal>>(
  ({ fetchState }) => {
    const animal = fetchState.status === 'success' ? fetchState.data : null;

    const displayName = useMemo(() => {
      if (!animal) return null;
      return animal.name || animal.species || 'Animal';
    }, [animal]);

    if (fetchState.status === 'pending') {
      return <LoadingState rows={1} />;
    }

    if (fetchState.status === 'error') {
      return <ErrorState error={fetchState.error} entityType="animal" />;
    }

    if (fetchState.status === 'idle' || !animal) {
      return <EmptyState entityType="animal" />;
    }

    return (
      <div className="divide-y divide-gray-100">
        <TooltipHeader icon={SparklesIcon} title={displayName || 'Animal'} />

        {(animal.species || animal.description) && (
          <TooltipSection withBorder>
            <div className="space-y-1.5 text-sm text-gray-900">
              {animal.species && animal.name && <div>{animal.species}</div>}
              {animal.description && <div>{animal.description}</div>}
            </div>
          </TooltipSection>
        )}
      </div>
    );
  },
);

AnimalTooltipContent.displayName = 'AnimalTooltipContent';

export const AnimalLink = createEntityLink<
  Animal,
  { id: string; matterId: string }
>({
  tooltipContent: AnimalTooltipContent,
  useQuery: enhancedApi.endpoints.animalsRetrieve.useQuery,
  getQueryArg: (id) => {
    const [matterId, animalId] = id.split(':');
    return { id: animalId || id, matterId: matterId || id };
  },
  displayName: 'AnimalLink',
});
